'use client'
import React, { useState } from 'react'
import IconButton from '@mui/material/IconButton'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItemText from '@mui/material/ListItemText'
import Box from '@mui/material/Box'
import Avatar from '@mui/material/Avatar'
import { styled } from '@mui/material/styles'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Link from 'next/link'
import ListItemButton from '@mui/material/ListItemButton'
import Image from 'next/image'
import ListItemIcon from '@mui/material/ListItemIcon'
import NewspaperIcon from '@mui/icons-material/Newspaper'
import SupervisedUserIcon from '@mui/icons-material/SupervisedUserCircle'
import CategoryIcon from '@mui/icons-material/Category'
import StorageIcon from '@mui/icons-material/Storage'
import useLogout from '@/hooks/useLogout'
import Typography from '@mui/material/Typography'
import { APP_NAMESPACE, TITLE } from '@/constants/AppConfig'
import Slide from '@mui/material/Slide'
import ThemeToggle from '@/components/ThemeToggle'
import { useCustomTheme } from '@/context/ThemeProvider'

import useAuth from '@/hooks/useAuth'
import useScrollTrigger from '@mui/material/useScrollTrigger'
import { StyledLink } from '@/styledComponents/StyledLink'
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  height: '64px', // Standard height for a navbar
  display: 'flex',
  justifyContent: 'center',
}))

const StyledToolbar = styled(Toolbar)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  height: '100%',
})

interface Props {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window?: () => Window
  children?: React.ReactElement<unknown>
}

function HideOnScroll(props: Props) {
  const { children, window } = props
  // Note that you normally won't need to set the window ref as useScrollTrigger
  // will default to window.
  // This is only being set here because the demo is in an iframe.
  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
  })

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children ?? <div />}
    </Slide>
  )
}

export default function Navbar() {
  const { darkPalette } = useCustomTheme()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(anchorEl)

  const { auth } = useAuth()
  const logout = useLogout()

  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open)
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  return (
    <HideOnScroll>
      <StyledAppBar
        position="fixed"
        sx={{
          backgroundColor: darkPalette.primary.main,
          boxShadow: 'none',
          borderBottom: darkPalette.divider,
          px: { xs: 1, sm: 2, md: 4, lg: 8, xl: 16 }, // margin left/right responsive
        }}
      >
        <StyledToolbar sx={{ pl: '0 !important', pr: '0 !important' }}>
          {auth && (
            <IconButton edge="start" color="inherit" aria-label="menu" onClick={toggleDrawer(!drawerOpen)}>
              {drawerOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          )}
          {auth && (
            <Drawer
              anchor="left"
              open={drawerOpen}
              onClose={toggleDrawer(false)}
              sx={{ zIndex: (theme) => theme.zIndex.appBar - 1 }}
            >
              <Box
                sx={{ marginTop: '64px', width: 250 }}
                role="presentation"
                onClick={toggleDrawer(!drawerOpen)}
                onKeyDown={toggleDrawer(!drawerOpen)}
              >
                <List>
                  <StyledLink href="/manage/news" passHref>
                    <ListItemButton>
                      <ListItemIcon>
                        <NewspaperIcon />
                      </ListItemIcon>
                      <ListItemText primary="News" />
                    </ListItemButton>
                  </StyledLink>
                  <StyledLink href="/manage/categories" passHref>
                    <ListItemButton>
                      <ListItemIcon>
                        <CategoryIcon />
                      </ListItemIcon>
                      <ListItemText primary="Categories" />
                    </ListItemButton>
                  </StyledLink>
                  <StyledLink href="/manage/tags" passHref>
                    <ListItemButton>
                      <ListItemIcon>
                        <CategoryIcon />
                      </ListItemIcon>
                      <ListItemText primary="Tags" />
                    </ListItemButton>
                  </StyledLink>
                  <StyledLink href="/manage/cache" passHref>
                    <ListItemButton>
                      <ListItemIcon>
                        <StorageIcon />
                      </ListItemIcon>
                      <ListItemText primary="Cache" />
                    </ListItemButton>
                  </StyledLink>
                  <ListItemButton>
                    <ListItemIcon>
                      <SupervisedUserIcon />
                    </ListItemIcon>
                    <ListItemText primary="User" />
                  </ListItemButton>
                </List>
              </Box>
            </Drawer>
          )}{' '}
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignContent: 'center' }}>
            <Link href="/" style={{ display: 'flex', alignItems: 'center', height: '100%' }} title={TITLE}>
              <Image
                src={`/${APP_NAMESPACE}/images/logo.png`}
                alt="Logo"
                width={180}
                height={50}
                priority={true}
                title={TITLE}
              />
            </Link>
          </Box>
          {/* Theme Toggle - Always visible */}
          <ThemeToggle />
          {
            auth ? (
              <>
                <IconButton
                  onClick={handleMenuClick}
                  color="inherit"
                  aria-label="Open user menu"
                  aria-haspopup="true"
                  aria-controls={isMenuOpen ? 'user-menu' : undefined}
                  aria-expanded={isMenuOpen ? 'true' : undefined}
                >
                  <Avatar alt={`${auth?.firstName || 'User'} profile`} />
                </IconButton>{' '}
                <Menu
                  id="user-menu"
                  anchorEl={anchorEl}
                  open={isMenuOpen}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  aria-labelledby="user-menu-button"
                >
                  <MenuItem>
                    <Link href="/profile" title="View Profile" style={{ textDecoration: 'none', color: 'inherit' }}>
                      <Typography variant="body1">Profile</Typography>
                    </Link>
                  </MenuItem>
                  <MenuItem onClick={logout}>
                    <Typography variant="body1">Logout</Typography>
                  </MenuItem>
                </Menu>
              </>
            ) : null
            // <Link href="/login" style={{ textDecoration: 'none', color: 'inherit' }}>
            //   <Typography
            //     variant="body1"
            //     sx={{
            //       textAlign: 'center',
            //       '&:hover': { fontWeight: 'bold' },
            //     }}
            //   >
            //     Login
            //   </Typography>
            // </Link>
          }
        </StyledToolbar>
      </StyledAppBar>
    </HideOnScroll>
  )
}
